import React, { useState, useRef } from "react";
import {
  Search,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Database,
  FileText,
  ArrowRight,
  History,
} from "lucide-react";
import { toast } from "react-hot-toast";
import documentSearchService, {
  KBSessionFileResponse,
} from "../../services/document-search.service";
import MarkdownRenderer from "./MarkdownRenderer";
import KnowledgeBaseHistoryTab, {
  KnowledgeBaseHistoryTabRef,
} from "./KnowledgeBaseHistoryTab";

interface KnowledgeBaseTabProps {
  documentStoreId: string;
}

const suggestedQueries = [
  "Summarize all documents in this folder",
  "What common topics are discussed across files?",
  "List key findings from each document",
  "Compare information across all files",
];

const KnowledgeBaseTab: React.FC<KnowledgeBaseTabProps> = ({
  documentStoreId,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    KBSessionFileResponse[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [copiedFileId, setCopiedFileId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const kbHistoryRef = useRef<KnowledgeBaseHistoryTabRef | null>(null);

  const handleSearch = async (query?: string) => {
    const q = query || searchQuery;
    if (!q.trim()) {
      toast.error("Please enter a search query");
      return;
    }
    if (query) setSearchQuery(query);

    setIsSearching(true);
    setSearchResults([]);
    setExpandedFiles(new Set());

    try {
      const response = await documentSearchService.createKBSession({
        document_store_id: documentStoreId,
        search_query: q,
      });

      if (response.success && response.data) {
        const session = response.data;
        const runs = session.search_runs || [];
        const latestRun = runs[runs.length - 1];
        if (latestRun) {
          const fileResponses = latestRun.file_responses || [];
          setSearchResults(fileResponses);
          const allFileIds = new Set(
            fileResponses.map((file) => file.file_id),
          );
          setExpandedFiles(allFileIds);
        }
        // Refresh history after new session is created
        if (kbHistoryRef.current) {
          kbHistoryRef.current.refreshHistory();
        }
      } else {
        toast.error("Search failed. Please try again.");
      }
    } catch (error: any) {
      console.error("Error during knowledge base search:", error);
      toast.error(error.response?.data?.message || "Failed to perform search");
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const toggleExpanded = (fileId: string) => {
    setExpandedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  const handleCopyResponse = (fileId: string, response: string) => {
    navigator.clipboard.writeText(response);
    setCopiedFileId(fileId);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedFileId(null), 2000);
  };

  const collapseAll = () => setExpandedFiles(new Set());
  const expandAll = () =>
    setExpandedFiles(new Set(searchResults.map((f) => f.file_id)));

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "xls":
      case "xlsx":
        return "📊";
      case "txt":
        return "📋";
      case "pptx":
      case "ppt":
        return "📑";
      case "csv":
        return "📈";
      default:
        return "📁";
    }
  };

  return (
    <div className="space-y-5">
      {/* Search Input Card */}
      <div className="bg-white rounded-md border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-600 to-blue-600 flex items-center justify-center">
            <Database className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="text-sm font-bold text-slate-900">
            Search across all files in this knowledge base
          </h2>
        </div>
        <div className="relative">
          <textarea
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="What would you like to find across all documents?"
            disabled={isSearching}
            rows={3}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 focus:bg-white transition-all resize-none disabled:opacity-60 disabled:cursor-not-allowed"
          />
          <div className="flex items-center justify-between mt-2.5">
            <p className="text-[10px] text-slate-400">
              Press Enter to search · Shift+Enter for new line
            </p>
            <button
              onClick={() => handleSearch()}
              disabled={isSearching || !searchQuery.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 disabled:from-slate-300 disabled:to-slate-400 text-white text-xs font-semibold rounded-md transition-all disabled:cursor-not-allowed"
            >
              {isSearching ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Searching all files...
                </>
              ) : (
                <>
                  <Search className="w-3.5 h-3.5" />
                  Search All Files
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isSearching && (
        <div className="bg-white rounded-md border border-violet-100 p-6">
          <div className="flex items-center gap-4">
            <div className="relative w-10 h-10 flex-shrink-0">
              <div className="absolute inset-0 rounded-full border-2 border-t-violet-600 border-r-transparent border-b-violet-400 border-l-transparent animate-spin"></div>
              <div className="absolute inset-2 rounded-full border-2 border-t-purple-400 border-r-transparent border-b-purple-600 border-l-transparent animate-[spin_3s_linear_infinite_reverse]"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-violet-600 animate-pulse" />
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">
                Analyzing all files...
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Searching through every document in your knowledge base
              </p>
            </div>
          </div>
          {/* Skeleton file cards */}
          <div className="mt-4 space-y-2">
            {[80, 65, 50].map((w, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-md"
              >
                <div className="w-8 h-8 rounded-md bg-slate-200 ai-shimmer flex-shrink-0"></div>
                <div className="flex-1 space-y-1.5">
                  <div
                    className={`h-3 rounded ai-shimmer`}
                    style={{ width: `${w}%`, animationDelay: `${i * 0.2}s` }}
                  ></div>
                  <div
                    className="h-2 rounded ai-shimmer w-[40%]"
                    style={{ animationDelay: `${i * 0.2 + 0.1}s` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && !isSearching && (
        <div className="space-y-3">
          {/* Results Header */}
          <div className="bg-white rounded-md border border-slate-200 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">
                  Results from {searchResults.length}{" "}
                  {searchResults.length === 1 ? "file" : "files"}
                </h3>
                <p className="text-[10px] text-slate-400">
                  Each file's response based on your query
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={expandAll}
                className="text-[11px] font-medium text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-50 transition-colors"
              >
                Expand all
              </button>
              <span className="text-slate-300">|</span>
              <button
                onClick={collapseAll}
                className="text-[11px] font-medium text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-50 transition-colors"
              >
                Collapse all
              </button>
            </div>
          </div>

          {/* File Cards */}
          {searchResults.map((file) => {
            const isExpanded = expandedFiles.has(file.file_id);
            return (
              <div
                key={file.file_id}
                className="bg-white rounded-md border border-slate-200 overflow-hidden"
              >
                {/* File Header */}
                <div
                  className="px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleExpanded(file.file_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-xl flex-shrink-0">
                        {getFileIcon(file.file_name)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-slate-900 truncate">
                          {file.file_name}
                        </h4>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {file.document_store_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isExpanded && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyResponse(file.file_id, file.response);
                          }}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${
                            copiedFileId === file.file_id
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                          }`}
                          title="Copy response"
                        >
                          {copiedFileId === file.file_id ? (
                            <>
                              <Check className="w-3 h-3" />
                              <span>Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      )}
                      <div
                        className={`p-1 rounded-md transition-colors ${isExpanded ? "text-violet-600" : "text-slate-400"}`}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* File Response */}
                {isExpanded && (
                  <div className="px-4 py-4 border-t border-slate-100 bg-slate-50/30">
                    <MarkdownRenderer content={file.response} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {searchResults.length === 0 && !isSearching && (
        <div className="bg-white rounded-md border border-slate-200 p-6">
          <div className="text-center mb-5">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 mb-3">
              <Database className="h-6 w-6 text-violet-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1">
              Search Your Knowledge Base
            </h3>
            <p className="text-xs text-slate-500">
              Query all files at once and get individual responses from each
              document
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {suggestedQueries.map((query) => (
              <button
                key={query}
                onClick={() => handleSearch(query)}
                className="group flex items-center gap-2 text-left px-3.5 py-2.5 text-xs text-slate-600 bg-slate-50 hover:bg-violet-50 hover:text-violet-700 border border-slate-200 hover:border-violet-200 rounded-md transition-all"
              >
                <ArrowRight className="w-3 h-3 text-slate-400 group-hover:text-violet-500 flex-shrink-0 transition-colors" />
                <span>{query}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Session History - Collapsible */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <History className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="text-left">
              <span className="text-sm font-bold text-slate-900 block">
                Session History
              </span>
              <span className="text-[10px] text-slate-400">
                View your past knowledge base searches
              </span>
            </div>
          </div>
          {showHistory ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </button>
        {showHistory && (
          <div className="border-t border-slate-100 px-5 py-4">
            <KnowledgeBaseHistoryTab
              documentStoreId={documentStoreId}
              ref={kbHistoryRef}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBaseTab;
